import { z } from "zod";
import { getParentField, getValueLabel } from "../../accessHelpers.js";
import { parseFields } from "../../schemaParser.js";
import { isArrayField, isObjectField } from "../../typeGuards.js";
import { getFieldMeta, getStateValue, isPartialFormState } from "../helpers.js";
import { isRequired } from "./isRequired.js";
/**
 * Validates the dependent fields in a form.
 *
 * @param data The form data to validate.
 * @param refinementContext The refinement context to add issues to.
 * @param shape The shape of the schema.
 * @returns The refinement context with any issues added.
 */
export function validateDependentFields(data, refinementContext, shape) {
    const fields = parseFields(shape, null);
    for (const field of fields) {
        if (data[field.field])
            continue;
        const settings = field.settings;
        if (!settings || !settings.dependsOn)
            continue;
        const parentField = getParentField(fields, settings.dependsOn);
        if (isRequired(parentField, settings.dependsOn, data[parentField.field])) {
            const parentLabel = getValueLabel(parentField, data);
            refinementContext.addIssue({
                code: z.ZodIssueCode.custom,
                message: `${getFieldMeta(field).label} is required when ${parentLabel} is selected.`,
                path: [field.field],
            });
        }
    }
    return refinementContext;
}
/**
 * Clears the excluded fields from the form state. This is useful when a field
 * was previously showing, but is now hidden because of a change in the parent
 * value.
 *
 * @template T The shape of the form
 * @param state The current form state.
 * @param fields The form fields.
 * @param globalState The global form state. (i.e., not the nested state)
 * @param globalFields The global form fields. (i.e., not the nested fields)
 * @returns The form state with the excluded fields' values cleared.
 * @throws Error If the field has children but is not an object in the state.
 */
export function clearExcludedFields(state, fields, globalState, globalFields) {
    const globalStateValue = globalState || state;
    const globalFieldsValue = globalFields || fields;
    const adjustedState = { ...state };
    for (const field of fields) {
        const currentStateValue = getStateValue(field.path, globalStateValue);
        const keyInState = field.field;
        if (isObjectField(field)) {
            if (!isPartialFormState(currentStateValue)) {
                throw new Error(`Invalid state for object field ${field.path}. The field has children defined but is not an object in the state.`);
            }
            // TODO: Fix up typing here
            adjustedState[keyInState] = clearExcludedFields(currentStateValue, field.children, globalStateValue, globalFieldsValue);
        }
        else if (isArrayField(field)) {
            if (!Array.isArray(currentStateValue)) {
                throw new Error(`Invalid state for array field ${field.path}. The child state is not an array. The child state: ${JSON.stringify(currentStateValue)}`);
            }
        }
        else {
            const { dependsOn } = field.settings || {};
            if (!dependsOn)
                continue;
            const parentField = getParentField(globalFieldsValue, dependsOn);
            const parentValue = getStateValue(parentField.path, globalStateValue);
            const fieldName = field.field;
            if (!isRequired(parentField, dependsOn, parentValue) &&
                fieldName in adjustedState) {
                delete adjustedState[fieldName];
            }
        }
    }
    return adjustedState;
}
//# sourceMappingURL=dependent.js.map